import { PACKAGE_EVENTS } from 'Sp/Angular/Events/Client/Gallery';
import { PRICE_SHEET_ITEM_EVENTS } from 'Sp/Angular/Events/Client/Gallery';
import { PACKAGE_SMALL_SCREEN_STATES } from 'Sp/Gallery';
import {
    getAssetMediaType,
    getProductDisplayMediaType,
    getProductMediaTypes,
    isAssetSellableAsProduct
} from 'ts/common/mediaType';
import { getMediaTypeMismatchKey } from 'ts/common/mediaTypeLabels';

/**
 * @ngdoc service
 * @name sp.client.gallery.service:spPackages
 * @requires $rootScope
 * @requires $window
 * @requires sp.client.common.service:SPCartData
 * @requires sp.client.common.service:SPModal
 * @requires sp.newcommon.service:spEventBus
 * @requires sp.newcommon.service:spPackage
 * @requires sp.client.gallery.filter:spPackageItemDetailFilter
 * @requires translateFilter
 *
 * @description
 * Helper functions for working with client package views
 */
export default [
    '$rootScope',
    '$q',
    '$window',
    'SPCartData',
    'SPModal',
    'spEventBus',
    'spPackage',
    'spPackageItemDetailFilter',
    'translateFilter',
    function spPackagesService(
        $rootScope,
        $q,
        $window,
        SPCartData,
        SPModal,
        spEventBus,
        spPackage,
        spPackageItemDetailFilter,
        translateFilter
    ) {
        const PACKAGE_SELECTED_ITEM = 'packageSelectedItem';
        const PACKAGE_SELECTION_STORAGE_KEY = 'packageSelectionState';
        const PACKAGE_SMALL_SCREEN_STATE_KEY = 'packageSmallScreenState';
        const PACKAGE_STORAGE_KEY = 'selectedPackage';
        const service = this;
        let isSinglePackageItemSelectedIndicator = false;
        let packages = [];
        let packagePhotos = [];
        let packageItemSelection = {};
        let pendingPackagePhotoAlbumMetadata = {};
        let packageItemErrors = {};
        let singlePoseError;
        let selectedPackage;
        let selectedPackageItem;
        let smallScreenState;
        let stopListeningForRouteChangeStart = null;
        let targetAnchorItem = {};

        restoreSelectedPackage();

        $rootScope.$on('$routeChangeSuccess', () => {
            clearSelectedPackagePhotos();
        });

        Object.assign(service, {
            addToCart,
            assignPhotoToAllPackageItems,
            assignSinglePosePhoto,
            canAddPackagesToCart,
            clearBackToCartListener,
            editPackageInCart,
            findStandInAsset,
            getAssignedPhoto,
            getAssignedSinglePhoto,
            getPackages,
            getPackageItemById,
            getPackageItemError,
            getPackageItemsSelectionState,
            getSinglePoseError,
            getSelectedPackage,
            getSelectedPackageItem,
            getSelectedPackageItemName,
            getSelectedPackagePhoto,
            getSmallScreenState,
            getPackageSelectedItemCount,
            getPackageTotalSelectableQuantity,
            getTargetPackageItem,
            isAPackagePhotoSelected,
            isPackagePhotoSelected,
            isSinglePackageItemSelected,
            openPackageSelectModal,
            mapPackageItemsToSelectionState,
            resetSelectedPackagePhotos,
            retrievePackages,
            selectIndividualPackagePhotoItem,
            selectPackageAllAlbumItem,
            selectPackageItem,
            selectPackageItemWithPhoto,
            selectPackagePhoto,
            selectPackageSingleItem,
            setSelectedPackage,
            setSmallScreenState,
            sumItemQuantities,
            togglePackagePhoto
        });

        return service;

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#addToCart
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Adds the currently selected (or the provided) package to the cart.
         *
         * @param {object} cartData Data required to add an item into the cart
         * @param {boolean} cartData.doesAcceptTerms If the user has accepted GDPR or not
         * @param {string} cartData.email The user's email
         * @param {function} cartData.onError Invoked with an object containing `needsEmail`
         *     or `errors` when adding data to the cart results in form errors from the API
         * @param {function} cartData.onSuccess Invoked when the package has been
         *     successfully added to the cart
         * @param {object=} cartData.packageToAdd Package to be added to the cart
         * @param {object=} cartData.albumForPackage Album context for the package photos
         * @param {object=} cartData.photoForPackage Will be assigned to all items in the
         *     package
         */
        function addToCart({
            albumForPackage = null,
            doesAcceptTerms,
            email,
            onError,
            onSuccess,
            packageToAdd = selectedPackage,
            photoForPackage = null
        }) {
            let selectionState = packageItemSelection;

            if (photoForPackage) {
                const albumMetadata = getAlbumMetadata(albumForPackage);

                selectionState = packageToAdd.items.reduce((selectionState, item) => {
                    const itemQuantity = spPackage.getPackageItemQuantity(item);

                    selectionState[item.id] = [];

                    for (let i = 0; i < itemQuantity; i++) {
                        selectionState[item.id].push({ photo: photoForPackage, ...albumMetadata });
                    }

                    return selectionState;
                }, {});
            }

            SPCartData.addToCart(
                { ...packageToAdd, selectionState, quantity: 1 },
                null,
                null,
                false,
                email,
                doesAcceptTerms,
                onSuccess,
                ({ errors, status, need_email }) => {
                    if (status === 'form_errors') {
                        if (typeof need_email === 'boolean' && need_email) {
                            onError({ needsEmail: true });

                            return;
                        }

                        onError({ errors });
                    }
                }
            );
        }

        function assignPhotosFromPackageInCart(cartItemId, packageInCart = null) {
            if (packageInCart) {
                assignPhotosToPackageItemSelectionFromPackageInCart(packageInCart);
            } else {
                SPCartData.getCartItems(({ cart }) => {
                    const packageInCart = cart.packageList.find(
                        ({ cartItemId: packageCartItemId }) => packageCartItemId === cartItemId
                    );

                    assignPhotosToPackageItemSelectionFromPackageInCart(packageInCart);
                }, angular.noop);
            }
        }

        function assignPhotosToPackageItemSelectionFromPackageInCart(packageInCart) {
            const priceSheetItemIndexCounts = {};

            packageInCart.items.forEach(({ boundItems, photo, priceSheetItemId }) => {
                const [boundItem] = boundItems;
                const index = priceSheetItemIndexCounts[priceSheetItemId] || 0;

                // The unguarded write. This re-hydrates a package the cart validator has already
                // accepted, so there is nothing to refuse - and the cart's photo payload does not
                // carry the media type the guard would read, so guarding here would refuse a
                // perfectly good video on the way back in.
                writePackageItemSelection(
                    photo,
                    priceSheetItemId,
                    index,
                    boundItem.albumId ? { albumId: boundItem.albumId } : undefined
                );

                priceSheetItemIndexCounts[priceSheetItemId] = index + 1;
            });
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#assignPhotoToAllPackageItems
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Assigns an event photo to all items in a package (except All Digitals in an Album)
         * and clears selected photos
         *
         * @param {object} photo Photo data
         * @param {object} metadata Optional metadata
         *
         * @returns {boolean} Whether any product in the package took the asset. False means
         *     every one of them refused it, so the caller should leave the tile armed.
         */
        function assignPhotoToAllPackageItems(photo, metadata = {}) {
            const albumMetadata =
                Object.keys(metadata).length > 0 ? metadata : pendingPackagePhotoAlbumMetadata;
            let didAssign = false;

            for (const itemId of getNonDigitalAlbumItemIds()) {
                const itemEntries = packageItemSelection[itemId] || [];

                itemEntries.forEach((_, index) => {
                    if (assignPhotoToPackageItemSelection(photo, itemId, index, albumMetadata)) {
                        didAssign = true;
                    }
                });
            }

            // Only once something took it: a staged asset every product refused has to survive, so
            // the tile it was staged from can show why instead of silently emptying itself.
            if (didAssign) {
                clearSelectedPackagePhotos();
            }

            return didAssign;
        }

        /** The products an asset is written to, everything but the all-album digitals. */
        function getNonDigitalAlbumItemIds() {
            return Object.keys(packageItemSelection).filter((id) => {
                return (
                    packageItemSelection[id][0] && !packageItemSelection[id][0].isAllAlbumDigital
                );
            });
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#assignSinglePosePhoto
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * The single pose assignment, with the package-level refusal that belongs to it. Both ways
         * in reach it - tapping an asset while the tile is armed, and clicking the tile with one
         * already staged - so a refusal reads the same whichever the client used.
         *
         * The sentence names the package rather than a product, because one asset for the whole
         * package is what single pose means, and the tile showing it stands for the package.
         *
         * @param {object} photo Photo, video or slideshow to assign, or null to clear
         * @param {object} metadata Optional metadata
         *
         * All or nothing: an asset any product refuses is refused outright. Writing it to the
         * products that take it would leave the others holding whatever pose they had before, and
         * the package would go to the cart with two different assets under one single pose.
         *
         * @returns {boolean} False when a product refused the asset, in which case nothing was
         *     written, it is still staged and #getSinglePoseError now reads the reason.
         */
        function assignSinglePosePhoto(photo, metadata = {}) {
            const isRefusedByAnyProduct =
                photo &&
                getNonDigitalAlbumItemIds().some((itemId) =>
                    getPackageItemMismatchKey(photo, getPackageItemById(itemId))
                );

            if (isRefusedByAnyProduct || !assignPhotoToAllPackageItems(photo, metadata)) {
                singlePoseError = photo ? getSinglePoseMismatchKey(photo) : undefined;

                return false;
            }

            singlePoseError = undefined;

            return true;
        }

        /**
         * The sentence for a single pose refusal, naming a media type the package really sells.
         *
         * Some product refused the asset to get here, so the package sells a media type the asset
         * is not, and whichever such type comes first is the pair the mismatch table has a
         * sentence for.
         * The package's display media type would not do: it collapses a mixed package to photo,
         * and a photo refused by a video-and-slideshow package would then ask for the photo/photo
         * sentence, which does not exist, and the alert would come up empty.
         *
         * Returns undefined when the package sells nothing else, which is the package with nothing
         * writable in it - refused by nobody, and with no media type to explain.
         */
        function getSinglePoseMismatchKey(photo) {
            const assetMediaType = getAssetMediaType(photo);
            const refusedForMediaType = getProductMediaTypes(selectedPackage).find(
                (mediaType) => mediaType !== assetMediaType
            );

            return refusedForMediaType
                ? getMediaTypeMismatchKey(assetMediaType, refusedForMediaType)
                : undefined;
        }

        function clearSelectedPackagePhotos() {
            packagePhotos = [];
            pendingPackagePhotoAlbumMetadata = {};
            singlePoseError = undefined;
        }

        /**
         * The guarded item-scoped write. Every client-initiated assignment funnels through here -
         * asset first and "+" first alike - so refusing a media type the product does not sell is
         * one check rather than one per flow.
         *
         * @returns {boolean} False when the assignment was refused, in which case nothing was
         *     written and an error sentence is now readable through #getPackageItemError.
         */
        function assignPhotoToPackageItemSelection(photo, itemId, itemIndex, metadata = {}) {
            const mismatchKey = getPackageItemMismatchKey(photo, getPackageItemById(itemId));

            if (mismatchKey) {
                packageItemErrors[itemId] = mismatchKey;

                return false;
            }

            writePackageItemSelection(photo, itemId, itemIndex, metadata);

            delete packageItemErrors[itemId];

            return true;
        }

        /**
         * The sentence explaining why this product cannot take this asset, or undefined when it
         * can.
         *
         * A pure predicate on purpose. Single pose mode fans one asset out across every product,
         * so letting this decide *where* the refusal is recorded would leave the single pose tile
         * reading an arbitrary product's message; each mode stores what it gets back instead.
         */
        function getPackageItemMismatchKey(photo, item) {
            // Deleting a tile's asset arrives with no photo, and there is nothing to check before
            // the selected package has been read.
            if (!photo || !selectedPackage || !item) {
                return undefined;
            }

            // An all-album or all-gallery product is skipped on purpose: its photo id names a
            // stand-in that exists only to give the cart line a photo_id, not something that gets
            // delivered - and the backend's Sp\Validate\Cart\PhotoMediaType returns early for
            // them for the same reason.
            if (
                spPackage.isShootProofFulfilledAllAlbumDownloadsDigitalItem(item) ||
                spPackage.isShootProofFulfilledAllGalleryDownloadsDigitalItem(item)
            ) {
                return undefined;
            }

            const assetMediaType = getAssetMediaType(photo);

            return isAssetSellableAsProduct(assetMediaType, item)
                ? undefined
                : getMediaTypeMismatchKey(assetMediaType, getProductDisplayMediaType(item));
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#getPackageItemError
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Translation key for the sentence explaining why this product refused the asset the
         * client picked for it, or undefined when it refused nothing.
         *
         * @param {number} itemId Item identifier
         */
        function getPackageItemError(itemId) {
            return packageItemErrors[itemId];
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#getSinglePoseError
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Translation key for the sentence explaining a refused single pose assignment. Its own
         * slot rather than a read across the per-product messages: single pose assigns one asset
         * to the whole package, so the package is the subject of the sentence, and the per-product
         * tiles those messages belong under are not rendered in this mode at all.
         */
        function getSinglePoseError() {
            return singlePoseError;
        }

        /**
         * The first asset `item` actually sells, else the first asset of any kind. An all-album or
         * all-gallery download needs a stand-in photo id purely to give the cart line one.
         */
        function findStandInAsset(photos, item) {
            const mediaType = getProductDisplayMediaType(item);

            return photos.find((photo) => getAssetMediaType(photo) === mediaType) ?? photos[0];
        }

        /** The raw item-scoped write, for callers that have already decided. */
        function writePackageItemSelection(photo, itemId, itemIndex, metadata = {}) {
            const { [itemId]: itemEntries = [] } = packageItemSelection;

            itemEntries[itemIndex] = {
                photo,
                ...metadata
            };

            packageItemSelection = {
                ...packageItemSelection,
                [itemId]: [...itemEntries]
            };
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#canAddPackagesToCart
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Determines if a package can be added to the cart.
         *
         * @returns {boolean} Returns true if the package setting is on
         *  and a package exists
         */
        function canAddPackagesToCart() {
            const { hasPackages } = $rootScope.eventData.settings;

            return hasPackages && Boolean(packages) && packages.length > 0;
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#clearBackToCartListener
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Clear route change listener created when editing package from cart
         */
        function clearBackToCartListener() {
            if (stopListeningForRouteChangeStart) {
                stopListeningForRouteChangeStart();
                stopListeningForRouteChangeStart = null;
            }
        }

        function deselectPackageItem() {
            selectedPackageItem = { itemId: null, itemIndex: null };
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#editPackageInCart
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Sets up a Package that exists in a Cart for editing.
         *
         * @param {object} packageInCart A Package that exists in the Cart
         */
        function editPackageInCart(packageInCart) {
            const { cartItemId } = packageInCart;
            const selectedPackageId = Number(packageInCart.priceSheetItemId);
            const priceSheetPackage = packages.find(({ id }) => id === selectedPackageId);

            stopListeningForRouteChangeStart = $rootScope.$on(
                '$routeChangeStart',
                handleEditInPackageRouteChange
            );

            setSelectedPackage({ ...priceSheetPackage, cartItemId });
            assignPhotosFromPackageInCart(cartItemId, packageInCart);
            setSelectedDigitalState(packageInCart, priceSheetPackage);
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#getAssignedSinglePhoto
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Retrieves the photo for the package when single pose is enabled. Excludes photos
         * associated with an "All Photos in Album" digital.
         *
         * @returns {object} Photo data
         */
        function getAssignedSinglePhoto() {
            const itemEntries = Object.values(packageItemSelection).find(
                ([firstItem]) => firstItem && !firstItem.isAllAlbumDigital && firstItem.photo
            );

            return itemEntries?.[0].photo;
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#getAssignedPhoto
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Returns the assigned photo for the provided package item and index.
         *
         * @param {number} itemId Item identifier
         * @param {number} itemIndex Index of of the item entry (tied to item quantity)
         * @returns {object|null} The assigned Photo
         */
        function getAssignedPhoto(itemId, itemIndex) {
            const packageMetadata = packageItemSelection[itemId][itemIndex] || {};
            const { photo = null } = packageMetadata;

            return photo;
        }

        function getPackageItemById(itemId) {
            const { items = [] } = selectedPackage || {};

            return items.find(({ id }) => id === Number(itemId));
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#getPackageItemsSelectionState
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Getter for the package item selected state.
         */
        function getPackageItemsSelectionState() {
            return packageItemSelection;
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#getPackages
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Getter for the packages.
         */
        function getPackages() {
            return packages;
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#getSelectedPackage
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Getter for the package mode state.
         */
        function getSelectedPackage() {
            return selectedPackage;
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#getSelectedPackagePhoto
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Retrieve the current selected package items photo
         *
         * @returns {object} The currently selected package photo
         */
        function getSelectedPackagePhoto() {
            const [selectedPhoto] = packagePhotos;

            return selectedPhoto;
        }

        function getSmallScreenState() {
            return smallScreenState;
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#getTargetPackageItem
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Getter for the selected package item anchor.
         *
         * @returns {object} The currently selected package item anchor
         */
        function getTargetPackageItem() {
            return targetAnchorItem;
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#getSelectedPackageItem
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Getter for the selected package item.
         *
         * @returns {object} The currently selected package item
         */
        function getSelectedPackageItem() {
            return selectedPackageItem;
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#getSelectedPackageItemName
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Getter for the selected package item's name.
         *
         * @returns {string} The currently selected package item's name
         */
        function getSelectedPackageItemName() {
            const { itemId } = getSelectedPackageItem();

            if (!itemId) {
                return '';
            }

            return spPackageItemDetailFilter(
                getSelectedPackage().items.find(({ id }) => id === itemId),
                false,
                false
            );
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#isAPackagePhotoSelected
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Indicates if a Photo is selected in Package Mode.
         *
         * @returns {boolean} If a Photo is selected in Package Mode
         */
        function isAPackagePhotoSelected() {
            return packagePhotos.length > 0;
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#isAPackagePhotoSelected
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Returns whether a single pose package item is selected
         *
         * @returns {boolean} Represents whether a single pose package item is
         *    selected
         */
        function isSinglePackageItemSelected() {
            return isSinglePackageItemSelectedIndicator;
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#isPackagePhotoSelected
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Determines if a package photo has been selected.
         *
         * @param {number} photoId Photo identifier
         */
        function isPackagePhotoSelected(photoId) {
            return Boolean(packagePhotos.find(({ id }) => id === photoId));
        }

        function getAlbumMetadata(album) {
            return album ? { albumId: Number(album.id) || null } : {};
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#getPackageSelectedItemCount
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Returns the count of package items which have a selected image.
         */
        function getPackageSelectedItemCount() {
            return Object.keys(packageItemSelection).reduce((selectedCount, packageItemId) => {
                const item = getPackageItemById(packageItemId);

                if (spPackage.isShootProofFulfilledAllGalleryDownloadsDigitalItem(item)) {
                    return selectedCount;
                }

                const itemEntries = packageItemSelection[packageItemId];

                for (const entry of itemEntries) {
                    if (entry.photo) {
                        selectedCount += 1;
                    }
                }

                return selectedCount;
            }, 0);
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#getPackageTotalSelectableQuantity
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Returns the total quantity of selectable items on the selected package. For example,
         * an "All Items" digital download is not a selectable item, while any individual bounds
         * size or "Individual Items" digital download are selectable items.
         *
         * @returns {number} The number of selectable items on the selected package
         */
        function getPackageTotalSelectableQuantity() {
            return selectedPackage.items.reduce((totalQuantity, item) => {
                if (item.quantity) {
                    totalQuantity += item.quantity;
                } else if (
                    item.individualPhotoDownload &&
                    item.individualPhotoDownload.quantity > 0
                ) {
                    totalQuantity += item.individualPhotoDownload.quantity;
                } else if (
                    item.allAlbumPhotosDownload &&
                    item.allAlbumPhotosDownload.quantity > 0
                ) {
                    totalQuantity += 1;
                }

                return totalQuantity;
            }, 0);
        }

        function handleEditInPackageRouteChange($event, next) {
            if (next.controller !== 'Cart') {
                return;
            }

            if ($window.confirm(translateFilter('backToCartConfirmation'))) {
                service.clearBackToCartListener();
                service.setSmallScreenState(PACKAGE_SMALL_SCREEN_STATES.PHOTO_VIEW);
                service.setSelectedPackage();
            } else {
                $event.preventDefault();
            }
        }

        function mapPackageItemsToSelectionState() {
            if (!selectedPackage) {
                return;
            }

            const { items = [] } = selectedPackage;

            packageItemSelection = items.reduce(
                (packageItemMap, item) => ({
                    ...packageItemMap,
                    [item.id]: spPackage.getInitialPackageItemSelectionState(item)
                }),
                {}
            );
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#openPackageSelectModal
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Opens the package select modal.
         */
        function openPackageSelectModal() {
            SPModal.open('main', {
                partialDirectory: 'package-select',
                type: 'dialog package-select'
            });
        }

        function restoreSelectedPackage() {
            const storedPackage = $window.sessionStorage.getItem(PACKAGE_STORAGE_KEY);
            const storedPackageSelectionState = $window.sessionStorage.getItem(
                PACKAGE_SELECTION_STORAGE_KEY
            );
            const storedSmallScreenState = $window.sessionStorage.getItem(
                PACKAGE_SMALL_SCREEN_STATE_KEY
            );
            const storedSelectedPackageItem = $window.sessionStorage.getItem(PACKAGE_SELECTED_ITEM);

            selectedPackage = storedPackage ? JSON.parse(storedPackage) : undefined;
            packageItemSelection = storedPackageSelectionState
                ? JSON.parse(storedPackageSelectionState)
                : {};
            smallScreenState = storedSmallScreenState
                ? JSON.parse(storedSmallScreenState)
                : PACKAGE_SMALL_SCREEN_STATES.PHOTO_VIEW;
            selectedPackageItem = storedSelectedPackageItem
                ? JSON.parse(storedSelectedPackageItem)
                : { itemId: null, itemIndex: null };

            if (selectedPackage && !packageItemSelection) {
                mapPackageItemsToSelectionState();
            }

            const { cartItemId } = selectedPackage || {};

            if (cartItemId) {
                assignPhotosFromPackageInCart(cartItemId);
            }
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#resetSelectedPackagePhotos
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Resets the selected package photos
         */
        function resetSelectedPackagePhotos() {
            clearSelectedPackagePhotos();
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#retrievePackages
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Retrieves the packages if the package setting is on.
         */
        function retrievePackages() {
            const { hasPackages, hasPriceSheet } = $rootScope.eventData.settings;

            if (hasPackages && hasPriceSheet) {
                $rootScope.$once(PRICE_SHEET_ITEM_EVENTS.LISTED, (_, listedPackages) => {
                    packages = listedPackages;
                });
                $rootScope.$emit(PRICE_SHEET_ITEM_EVENTS.LIST, { filterIsPackage: true });
            }
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#isSinglePackageItemSelected
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Toggles single pose package item selection
         */
        function selectPackageSingleItem() {
            isSinglePackageItemSelectedIndicator = !isSinglePackageItemSelectedIndicator;
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#selectPackageItem
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Sets the selected package item.
         *
         * @param {number} itemId Item identifier
         * @param {number} itemIndex Index of of the item entry (tied to item quantity)
         */
        function selectPackageItem(itemId, itemIndex) {
            targetAnchorItem = {
                itemId,
                itemIndex
            };
            selectedPackageItem = {
                itemId,
                itemIndex
            };

            // Re-arming the tile is the client starting over on this product, so the sentence
            // explaining the last refusal has served its purpose.
            delete packageItemErrors[itemId];
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#selectPackageAllAlbumItem
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Selects an individual package all photod in album item, leaving the album selected
         *
         * @param {number} albumId Album identifier
         * @param {number} itemId Item identifier
         * @param {number} itemIndex Index of the item entry (tied to item quantity)
         * @param {object} photo Current photo to assign to the album as a stand in
         */
        function selectPackageAllAlbumItem(itemId, itemIndex, albumId, photo) {
            const { [itemId]: itemEntries = [] } = packageItemSelection;

            itemEntries[itemIndex] = {
                id: itemId,
                itemIndex,
                albumId,
                photo,
                isAllAlbumDigital: true
            };

            packageItemSelection = {
                ...packageItemSelection,
                [itemId]: [...itemEntries]
            };
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#selectIndividualPackagePhotoItem
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Selects an individual package photo item, leaving the photo selected.
         *
         * @param {number} itemId Item identifier
         * @param {number} itemIndex Index of of the item entry (tied to item quantity)
         * @param {object|null} album The album of the view
         */
        function selectIndividualPackagePhotoItem(itemId, itemIndex, album) {
            const metadata = getAlbumMetadata(album);
            const [photo] = packagePhotos;

            if (!photo) {
                return false;
            }

            // The staged asset is deliberately left staged on a refusal, so the next tile the
            // client taps can take it without them having to pick it again.
            return assignPhotoToPackageItemSelection(photo, itemId, itemIndex, metadata);
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#selectPackageItemWithPhoto
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Correlates a price sheet item entry (where there is an entry for each
         * value represented in quantity) with a photo within the gallery.
         *
         * @param {number} itemId Item identifier
         * @param {number} itemIndex Index of of the item entry (tied to item quantity)
         * @param {boolean=} isDelete Represents whether this is a delete operation
         */
        function selectPackageItemWithPhoto(itemId, itemIndex, isDelete = false) {
            if (isDelete) {
                assignPhotoToPackageItemSelection(null, itemId, itemIndex);

                return true;
            }

            const [photo] = packagePhotos;

            if (!photo) {
                return false;
            }

            if (
                !assignPhotoToPackageItemSelection(
                    photo,
                    itemId,
                    itemIndex,
                    pendingPackagePhotoAlbumMetadata
                )
            ) {
                return false;
            }

            clearSelectedPackagePhotos();

            return true;
        }

        /**
         * Forces selection of a package photo
         * @param {object} photo Photo data
         * @param {object=} album Album the photo was selected from
         */
        function selectPackagePhoto(photo, album) {
            packagePhotos = [photo];
            pendingPackagePhotoAlbumMetadata = getAlbumMetadata(album);
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#setSelectedDigitalState
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Sets the current selected digitals package items.
         *
         * @param {object} packageInCart A Package that exists in the Cart
         * @param {object} priceSheetPackage The current Price Sheet Package
         */
        function setSelectedDigitalState(packageInCart, priceSheetPackage) {
            const { items } = packageInCart;
            const digitalPackageItemAllAlbum = priceSheetPackage.items.filter(
                (packageItem) =>
                    packageItem.allAlbumPhotosDownload &&
                    packageItem.allAlbumPhotosDownload.quantity > 0
            );

            if (digitalPackageItemAllAlbum.length) {
                let digitalAllAlbum = [];

                for (let i = 0; i < digitalPackageItemAllAlbum.length; i++) {
                    for (let j = 0; j < items.length; j++) {
                        if (digitalPackageItemAllAlbum[i].id === items[j].priceSheetItemId) {
                            digitalAllAlbum.push(items[j]);
                            break;
                        }
                    }
                }

                if (digitalAllAlbum.length) {
                    for (let i = 0; i < digitalAllAlbum.length; i++) {
                        selectPackageAllAlbumItem(
                            digitalAllAlbum[i].priceSheetItemId,
                            0,
                            digitalAllAlbum[i].boundItems[0].albumId,
                            digitalAllAlbum[i].photo
                        );
                    }
                }
            }
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#setSelectedPackage
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Sets the currently selected package.
         *
         * @param {object} package Selected package data
         */
        function setSelectedPackage(packageData = null) {
            selectedPackage = packageData;

            if (packageData === null) {
                $window.sessionStorage.removeItem(PACKAGE_STORAGE_KEY);
                $window.sessionStorage.removeItem(PACKAGE_SELECTION_STORAGE_KEY);
                $window.sessionStorage.removeItem(PACKAGE_SMALL_SCREEN_STATE_KEY);
                $window.sessionStorage.removeItem(PACKAGE_SELECTED_ITEM);
            } else {
                $window.sessionStorage.setItem(PACKAGE_STORAGE_KEY, JSON.stringify(packageData));
                $window.sessionStorage.setItem(
                    PACKAGE_SMALL_SCREEN_STATE_KEY,
                    JSON.stringify(smallScreenState)
                );
                $window.sessionStorage.setItem(
                    PACKAGE_SELECTED_ITEM,
                    JSON.stringify(selectedPackageItem)
                );

                if (packageItemSelection) {
                    $window.sessionStorage.setItem(
                        PACKAGE_SELECTION_STORAGE_KEY,
                        JSON.stringify(packageItemSelection)
                    );
                }
            }

            clearSelectedPackagePhotos();
            packageItemErrors = {};
            singlePoseError = undefined;

            deselectPackageItem();

            if (packageData && typeof packageItemSelection !== undefined) {
                mapPackageItemsToSelectionState();
            }

            $rootScope.$emit(PACKAGE_EVENTS.SELECT);
        }

        function setSmallScreenState(screenState) {
            smallScreenState = screenState;

            // Force the package builder to render, so it displays the current view on small screens (either Packages or Photos)
            $rootScope.$evalAsync(() => {
                $rootScope.updateCount = ($rootScope.updateCount ?? 0) + 1;
            });
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#sumItemQuantities
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Calculates the sum of quantities on a collection of items on a package.
         *
         * @param {object[]} items Collection of price sheet items
         */
        function sumItemQuantities(items) {
            return items.reduce((sum, item) => {
                return sum + spPackage.getPackageItemQuantity(item);
            }, 0);
        }

        /**
         * @ngdoc method
         * @name sp.client.gallery.service:spPackages#togglePackagePhoto
         * @methodOf sp.client.gallery.service:spPackages
         *
         * @description
         * Toggles selection of a package photo, currently supporting only a
         * single photo selected at once.
         *
         * @param {object} targetPhoto Target photo to toggle
         * @param {object|null} album The current photo the album is in
         */
        function togglePackagePhoto(targetPhoto, album) {
            const metadata = getAlbumMetadata(album);

            if (packagePhotos.length) {
                const [firstPackagePhoto] = packagePhotos;

                if (firstPackagePhoto.id === targetPhoto.id) {
                    clearSelectedPackagePhotos();

                    return;
                }
            } else if (isSinglePackageItemSelected()) {
                // Refused by a product: stay armed on the photo view so another can be picked.
                if (!assignSinglePosePhoto(targetPhoto, metadata)) {
                    return;
                }

                selectPackageSingleItem();
                setSmallScreenState(PACKAGE_SMALL_SCREEN_STATES.PACKAGE_VIEW);

                return;
            } else if (getSelectedPackageItem()) {
                const { itemId, itemIndex } = getSelectedPackageItem();

                if (itemId && itemIndex >= 0) {
                    // Refused: leave the tile armed rather than deselecting it, so the next tap
                    // can succeed, and stay on the photo view on a small screen.
                    if (
                        !assignPhotoToPackageItemSelection(targetPhoto, itemId, itemIndex, metadata)
                    ) {
                        return;
                    }

                    deselectPackageItem();
                    setSmallScreenState(PACKAGE_SMALL_SCREEN_STATES.PACKAGE_VIEW);

                    return;
                }
            }

            packagePhotos = [targetPhoto];
            pendingPackagePhotoAlbumMetadata = metadata;
        }
    }
];
